import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, ArrowUpRight } from "lucide-react";
import { Reveal } from "../../lib/motion";
import { COMPANY, SERVICES } from "../../data/site";

const initial = { name: "", email: "", phone: "", service: "Mutual Funds", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    const lines = [
      "New enquiry — Aryaki Finserv",
      "",
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      form.email ? `Email: ${form.email}` : null,
      `Interested in: ${form.service}`,
      "",
      "Message:",
      form.message,
    ].filter(Boolean);
    const url = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener");
    toast.success("Opening WhatsApp — tap send to reach E.L.S. Ranjan.");
    setForm(initial);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-[var(--accent)]/8 blur-[130px]" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* Left info */}
        <div className="lg:col-span-5">
          <Reveal><p className="overline mb-4">Let&rsquo;s talk</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[var(--text)] leading-[1.02]">
              Book a free <span className="text-gold italic">consultation.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 text-white/60 text-base leading-relaxed max-w-md">
              Share a few details and {COMPANY.planner} will get back to you to understand your goals —
              no obligation, no jargon.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            <a data-testid="contact-phone" href={`tel:${COMPANY.phone}`} className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-gold group-hover:border-[var(--accent)] transition-colors">
                <Phone size={18} />
              </span>
              <span className="text-white/80 group-hover:text-gold transition-colors">{COMPANY.phone}</span>
            </a>
            <a data-testid="contact-email" href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-gold group-hover:border-[var(--accent)] transition-colors">
                <Mail size={18} />
              </span>
              <span className="text-white/80 group-hover:text-gold transition-colors">{COMPANY.email}</span>
            </a>
            <a data-testid="contact-whatsapp" href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-gold group-hover:border-[var(--accent)] transition-colors">
                <MessageCircle size={18} />
              </span>
              <span className="text-white/80 group-hover:text-gold transition-colors">Chat on WhatsApp</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-gold">
                <MapPin size={18} />
              </span>
              <span className="text-white/80">{COMPANY.address}</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <Reveal delay={0.15} className="lg:col-span-7">
          <form data-testid="contact-form" onSubmit={submit} className="rounded-3xl bg-[#111827] border border-[#2A3441] p-8 lg:p-10 space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <LineInput label="Full name *" testid="input-name" value={form.name} onChange={set("name")} placeholder="Your name" />
              <LineInput label="Phone *" testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
            </div>
            <LineInput label="Email *" type="email" testid="input-email" value={form.email} onChange={set("email")} placeholder="you@email.com" />

            <div>
              <label className="text-sm text-white/60">I&rsquo;m interested in</label>
              <select
                data-testid="input-service"
                value={form.service}
                onChange={set("service")}
                className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-[var(--text)] outline-none focus:border-[var(--accent)] transition-colors"
              >
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title} className="bg-[#111827]">
                    {s.title}
                  </option>
                ))}
                <option value="General Enquiry" className="bg-[#111827]">General Enquiry</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-white/60">Message *</label>
              <textarea
                data-testid="input-message"
                value={form.message}
                onChange={set("message")}
                rows={4}
                placeholder="Tell us a little about your goals..."
                className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-[var(--text)] placeholder:text-white/30 outline-none focus:border-[var(--accent)] transition-colors resize-none"
              />
            </div>

            <motion.button
              data-testid="contact-submit"
              type="submit"
              whileTap={{ scale: 0.98 }}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[var(--accent)] text-[#0A0F1C] font-medium hover:bg-[#e6c65a] transition-colors duration-300"
            >
              <MessageCircle size={18} />
              Send enquiry
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const LineInput = ({ label, testid, type = "text", ...rest }) => (
  <div>
    <label className="text-sm text-white/60">{label}</label>
    <input
      data-testid={testid}
      type={type}
      className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-[var(--text)] placeholder:text-white/30 outline-none focus:border-[var(--accent)] transition-colors"
      {...rest}
    />
  </div>
);
